import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/errors/use-toast';

export interface AddPrivateRelatedQuestionPayload {
  researchId: string;
  questionText: string;
}

export const useAddPrivateRelatedQuestion = () => {
  const queryClient = useQueryClient();

  const addRelatedQuestionMutation = useMutation({
    mutationFn: async (payload: AddPrivateRelatedQuestionPayload) => {
      console.log('[useAddPrivateRelatedQuestion] Adding new related question to private research:', payload);

      // First, get the current private research to access its related_questions
      const { data: currentResearch, error: fetchError } = await supabase
        .from('workbook_research')
        .select('related_questions')
        .eq('id', payload.researchId)
        .single();

      if (fetchError) {
        console.error('[useAddPrivateRelatedQuestion] Error fetching current private research:', fetchError);
        throw new Error(`Failed to fetch current private research: ${fetchError.message}`);
      }

      if (!currentResearch) {
        throw new Error('Private research not found');
      }

      // Get the current related questions array - handle both old string[] and new mixed format
      let currentQuestions: any[] = [];
      if (Array.isArray(currentResearch.related_questions)) {
        currentQuestions = currentResearch.related_questions;
      } else if (currentResearch.related_questions) {
        // Handle case where it might be stored as jsonb but needs parsing
        try {
          currentQuestions = JSON.parse(JSON.stringify(currentResearch.related_questions));
        } catch (parseError) {
          console.warn('[useAddPrivateRelatedQuestion] Failed to parse related_questions, treating as empty array');
          currentQuestions = [];
        }
      }

      // Create the updated questions array with the new question at the beginning
      const updatedQuestions = [payload.questionText, ...currentQuestions];

      console.log('[useAddPrivateRelatedQuestion] Updated questions array:', updatedQuestions);

      // Update the private research in the database
      const { data, error: updateError } = await supabase
        .from('workbook_research')
        .update({
          related_questions: updatedQuestions,
          updated_at: new Date().toISOString()
        })
        .eq('id', payload.researchId)
        .select()
        .single();

      if (updateError) {
        console.error('[useAddPrivateRelatedQuestion] Error updating private research:', updateError);
        throw new Error(`Failed to update private research: ${updateError.message}`);
      }

      console.log('[useAddPrivateRelatedQuestion] Private research updated successfully:', data);
      return data;
    },
    onSuccess: (data, payload) => {
      console.log('[useAddPrivateRelatedQuestion] Mutation succeeded, invalidating private research queries');
      
      // CRITICAL: Invalidate private research item details cache for immediate UI update
      queryClient.invalidateQueries({
        queryKey: ['private-research-item-details', payload.researchId],
        exact: true,
        refetchType: 'all'
      });
      
      // Also invalidate the general item details cache pattern
      queryClient.invalidateQueries({
        queryKey: ['private-research-item-details'],
        exact: false,
        refetchType: 'all'
      });
      
      // Invalidate private research generator queries (main page cache)
      queryClient.invalidateQueries({
        queryKey: ['private-research-generator'],
        exact: false,
        refetchType: 'all'
      });
      
      // Invalidate private research count queries
      queryClient.invalidateQueries({
        queryKey: ['private-research-generator-count'],
        exact: false,
        refetchType: 'all'
      });
      
      // Invalidate universal storage queries
      queryClient.invalidateQueries({
        queryKey: ['universal-storage-items'],
        exact: false,
        refetchType: 'all'
      });
      
      queryClient.invalidateQueries({
        queryKey: ['universal-library-data'],
        exact: false,
        refetchType: 'all'
      });
      
      // Invalidate library counts (for sidebar counters)
      queryClient.invalidateQueries({
        queryKey: ['universal-library-counts'],
        exact: false,
        refetchType: 'all'
      });

      toast({
        title: 'Question added',
        description: 'Your custom question has been added to the related questions',
      });
    },
    onError: (error: any) => {
      console.error('[useAddPrivateRelatedQuestion] Mutation failed:', error);
      toast({
        title: 'Failed to add question',
        description: error.message || 'Failed to add the question to related questions',
        variant: 'destructive',
      });
    }
  });

  return {
    addRelatedQuestion: addRelatedQuestionMutation.mutate,
    isAdding: addRelatedQuestionMutation.isPending,
    error: addRelatedQuestionMutation.error
  };
}; 